import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";

const HERO = "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function formatErr(detail) {
  if (!detail) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { t } = useI18n();
  const { doLogin, doRegister } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);

  const googleEnabled = process.env.REACT_APP_ENABLE_GOOGLE_LOGIN !== "false";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && (!terms || !privacy)) {
      toast.error(t("reg.accept.required"));
      return;
    }
    setLoading(true);
    try {
      const user = mode === "login"
        ? await doLogin({ email: form.email, password: form.password })
        : await doRegister({ ...form, accept_terms: terms, accept_privacy: privacy });
      toast.success(`Benvenuto, ${user.name}!`);
      if (["super_admin", "admin", "staff"].includes(user.role)) navigate("/gestione");
      else if (user.role === "operator") navigate("/operatore");
      else navigate("/account");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-10 items-center">
        <div className="hidden lg:block relative rounded-3xl overflow-hidden h-[520px]">
          <img src={HERO} alt="Vieste" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 text-white">
            <h2 className="font-heading font-semibold text-3xl">Vivi Vieste. Scopri il Gargano.</h2>
            <p className="text-white/80 mt-2">Accedi per gestire le tue prenotazioni.</p>
          </div>
        </div>
        <div className="rounded-3xl border border-slate-100 shadow-lg p-8 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-primary">👤 Voglio vivere il Gargano</div>
              <p className="text-xs text-slate-500 mt-1">Scopri e prenota il Gargano come cliente.</p>
            </div>
            <Link to="/registrati/operatore" data-testid="operator-path-card" className="rounded-2xl border-2 border-slate-200 hover:border-secondary p-4 transition-colors block">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-700">🏢 Voglio entrare in Cristalda Experience</div>
              <p className="text-xs text-slate-500 mt-1">Porta la tua attività: registrati come operatore →</p>
            </Link>
          </div>
          <div className="flex gap-2 mb-6 bg-slate-100 rounded-full p-1">
            <button type="button" onClick={() => setMode("login")} data-testid="tab-login" className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${mode === "login" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}>{t("nav.login")}</button>
            <button type="button" onClick={() => setMode("register")} data-testid="tab-register" className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${mode === "register" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}>Registrati</button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input required placeholder={t("checkout.name")} data-testid="auth-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-secondary" />
            )}
            <input required type="email" placeholder="Email" data-testid="auth-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-secondary" />
            <input required type="password" placeholder="Password" data-testid="auth-password" value={form.password} onChange={(e) => set("password", e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-secondary" />
            {mode === "register" && (
              <div className="space-y-2.5 pt-1">
                <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" required data-testid="accept-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
                  <span>{t("reg.accept.terms")} <Link to="/legale/termini" target="_blank" className="font-semibold text-primary underline underline-offset-2">Termini &amp; Condizioni</Link></span>
                </label>
                <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" required data-testid="accept-privacy" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
                  <span>{t("reg.accept.privacy")} <Link to="/legale/privacy" target="_blank" className="font-semibold text-primary underline underline-offset-2">Privacy Policy</Link></span>
                </label>
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full rounded-full bg-primary text-white font-semibold py-3 hover:bg-primary/90 transition-colors disabled:opacity-60">
              {loading ? t("common.loading") : mode === "login" ? t("nav.login") : "Crea account"}
            </button>
          </form>
          {googleEnabled && (
            <>
              <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-slate-100" /><span className="text-xs text-slate-400">oppure</span><div className="flex-1 h-px bg-slate-100" /></div>
              <button onClick={googleLogin} data-testid="google-login-btn" className="w-full rounded-full border border-slate-200 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continua con Google
              </button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
